import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const BATCH_SIZE = 50;

const CsvImportModal = ({ isOpen, onClose, title, placeholder, importChunk, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('file'); // 'file' | 'paste'
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [warnings, setWarnings] = useState([]);
  const [totalImported, setTotalImported] = useState(0);
  const [totalSkipped, setTotalSkipped] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const chunkCSV = (csv) => {
    const lines = csv.trim().split('\n');
    if (lines.length <= 1) return [];
    const header = lines[0];
    const rows = lines.slice(1);
    const chunks = [];
    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const chunkRows = rows.slice(i, i + BATCH_SIZE);
      chunks.push([header, ...chunkRows].join('\n'));
    }
    return chunks;
  };

  const readFileText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

  const resetState = () => {
    setActiveTab('file');
    setSelectedFile(null);
    setCsvContent('');
    setIsImporting(false);
    setIsWaiting(false);
    setProgress({ current: 0, total: 0 });
    setWarnings([]);
    setTotalImported(0);
    setTotalSkipped(0);
    setIsComplete(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        resetState();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleImport = async () => {
    try {
      setIsImporting(true);
      setIsComplete(false);
      let data = '';
      if (activeTab === 'file') {
        if (!selectedFile) {
          toast.error('Please select a CSV file');
          setIsImporting(false);
          return;
        }
        if (selectedFile.type && selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
          toast.error('Please select a CSV file');
          setIsImporting(false);
          return;
        }
        data = await readFileText(selectedFile);
      } else {
        if (!csvContent.trim()) {
          toast.error('Please paste CSV content');
          setIsImporting(false);
          return;
        }
        data = csvContent;
      }

      const chunks = chunkCSV(data);
      if (chunks.length === 0) {
        toast.error('No data rows found');
        setIsImporting(false);
        return;
      }

      setWarnings([]);
      setProgress({ current: 0, total: chunks.length });
      setTotalImported(0);
      setTotalSkipped(0);

      let warningsAcc = [];
      let importedAcc = 0;
      let skippedAcc = 0;

      for (let i = 0; i < chunks.length; i++) {
        const res = await importChunk(chunks[i]);
        if (!res?.success) {
          throw new Error(res?.error || 'Import failed');
        }
        if (Array.isArray(res.warnings)) warningsAcc = warningsAcc.concat(res.warnings);
        importedAcc += res.importedCount || 0;
        skippedAcc += res.skippedCount || 0;
        setWarnings(warningsAcc);
        setTotalImported(importedAcc);
        setTotalSkipped(skippedAcc);
        setProgress({ current: i + 1, total: chunks.length });
        if (i < chunks.length - 1) {
          setIsWaiting(true);
          await delay(1000);
          setIsWaiting(false);
        }
      }

      toast.success(`Imported ${importedAcc} rows${skippedAcc ? `, skipped ${skippedAcc}` : ''}`);
      if (onSuccess) onSuccess();
      setIsComplete(true);
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
            <button onClick={() => { resetState(); onClose(); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!isComplete && (
            <>
              <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('file')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'file'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Upload File
                  </button>
                  <button
                    onClick={() => setActiveTab('paste')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'paste'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Paste Content
                  </button>
                </nav>
              </div>

              {activeTab === 'file' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    disabled={isImporting}
                    className="block w-full text-sm text-gray-500 dark:text-gray-400
                               file:mr-4 file:py-2 file:px-4
                               file:rounded-md file:border-0
                               file:text-sm file:font-semibold
                               file:bg-blue-50 file:text-blue-700
                               dark:file:bg-blue-900/20 dark:file:text-blue-300
                               hover:file:bg-blue-100 dark:hover:file:bg-blue-900/30
                               file:cursor-pointer cursor-pointer
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {selectedFile && (
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'paste' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Paste CSV Content</label>
                  <textarea
                    value={csvContent}
                    onChange={(e) => setCsvContent(e.target.value)}
                    disabled={isImporting}
                    placeholder={placeholder}
                    rows={10}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              )}

              {isImporting && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 mb-2">
                    <span>Processing...</span>
                    <span>{progress.current} / {progress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: progress.total > 0 
                          ? `${(progress.current / progress.total) * 100}%` 
                          : '0%'
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-1">
                    <span>Imported: {totalImported}</span>
                    <span>Skipped: {totalSkipped}</span>
                    <span>Warnings: {warnings.length}</span>
                  </div>
                  {isWaiting && (
                    <div className="text-xs text-gray-500 mt-1">Waiting between batches...</div>
                  )}
                </div>
              )}

              {warnings.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">Warnings ({warnings.length})</h4>
                  <div className="max-h-40 overflow-y-auto bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                    {warnings.map((w, idx) => (
                      <div key={idx} className="text-xs text-yellow-800 dark:text-yellow-200 mb-1">
                        <span className="font-semibold">Row {w.rowNumber}:</span> <span className="text-yellow-600 dark:text-yellow-300">{w.ticker}</span> - {w.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button onClick={() => { resetState(); onClose(); }} disabled={isImporting} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2">Cancel</button>
                <button onClick={handleImport} disabled={isImporting || (activeTab === 'file' ? !selectedFile : !csvContent.trim())} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">{isImporting ? 'Importing...' : 'Import'}</button>
              </div>
            </>
          )}

          {isComplete && (
            <>
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Summary</h4>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <div>
                    <span className="font-semibold">Rows:</span> {totalImported} imported, {totalSkipped} skipped
                  </div>
                  <div>
                    <span className="font-semibold">Warnings:</span> {warnings.length}
                  </div>
                </div>
              </div>

              {warnings.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">Warnings ({warnings.length})</h4>
                  <div className="max-h-40 overflow-y-auto bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                    {warnings.map((w, idx) => (
                      <div key={idx} className="text-xs text-yellow-800 dark:text-yellow-200 mb-1">
                        <span className="font-semibold">Row {w.rowNumber}:</span> <span className="text-yellow-600 dark:text-yellow-300">{w.ticker}</span> - {w.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { resetState(); onClose(); }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  Close & Finish
                </button>
                <button
                  onClick={resetState}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  New Import
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
